import React, { useEffect, useRef, useCallback } from 'react';

interface WordDefinitionProps {
    word: string;
    onClose: () => void;
    position: { x: number; y: number };
}

const WordDefinition: React.FC<WordDefinitionProps> = ({ word, onClose, position }) => {
    const [definition, setDefinition] = React.useState<string>('');
    const [loading, setLoading] = React.useState(true);
    const popupRef = useRef<HTMLDivElement>(null);

    const calculatePosition = useCallback(() => {
        if (!popupRef.current) return {};
        
        const popup = popupRef.current;
        const viewport = {
            width: window.innerWidth,
            height: window.innerHeight
        };
        
        // Get popup dimensions
        const popupRect = popup.getBoundingClientRect();
        const popupWidth = popupRect.width;
        const popupHeight = popupRect.height;
        
        // Calculate initial positions
        let left = position.x;
        let top = position.y + 20; // 20px offset from cursor
        
        // Adjust horizontal position if needed
        if (left + popupWidth / 2 > viewport.width) {
            left = viewport.width - popupWidth - 10; // 10px padding from right edge
        } else if (left - popupWidth / 2 < 0) {
            left = popupWidth / 2 + 10; // 10px padding from left edge
        }
        
        // Adjust vertical position if needed
        if (top + popupHeight > viewport.height) {
            top = position.y - popupHeight - 10; // Show above cursor
        }
        
        return {
            left: `${left}px`,
            top: `${top}px`,
            transform: 'translate(-50%, 0)'
        };
    }, [position]);

    useEffect(() => {
        const fetchDefinition = async () => {
            try {
                const response = await fetch(`https://xianyou.uk/youdaoapi/result?word=${word}&lang=en`, {
                    headers: {
                        'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36',
                        'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
                        'Accept-Language': 'en-US,en;q=0.5',
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache'
                    }
                });
                const html = await response.text();
                
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                
                const definitionElement = doc.querySelector('.simple.dict-module .trans-container');
                
                if (definitionElement) {
                    const cleanHtml = definitionElement.outerHTML.replace(/\s+data-v-\w+=""/g, '');
                    setDefinition(cleanHtml);
                } else {
                    setDefinition('<div>No definition found</div>');
                }
            } catch (error) {
                console.error('Error fetching definition:', error);
                setDefinition('<div>Error loading definition</div>');
            } finally {
                setLoading(false);
            }
        };

        fetchDefinition();
    }, [word]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        const handleScroll = () => {
            onClose();
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('scroll', handleScroll, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('scroll', handleScroll, true);
        };
    }, [onClose]);

    useEffect(() => {
        if (!loading && popupRef.current) {
            const newPosition = calculatePosition();
            Object.assign(popupRef.current.style, newPosition);
        }
    }, [loading, position, calculatePosition]);

    return (
        <div
            ref={popupRef}
            className="fixed bg-white rounded-lg shadow-xl p-4 z-50 max-w-md"
            style={{
                left: `${position.x}px`,
                top: `${position.y + 20}px`,
                transform: 'translate(-50%, 0)'
            }}
        >
            {loading ? (
                <div className="flex items-center justify-center h-20">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                </div>
            ) : (
                <div 
                    className="definition-content"
                    dangerouslySetInnerHTML={{ __html: definition }}
                />
            )}
        </div>
    );
};

export default WordDefinition; 